import axios, { AxiosInstance } from "axios";
import { from, Observable } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { config } from "@/utils/config";
import { AxiosErrorWithResponse, intercept } from "@/api/AxiosInterceptors";
import { handleErrs } from "@/api/ErrHandlers";
import { MeErrHandlers } from "@/api/userErrHandlers/ErrHandlers";
import {
  CheckoutSessionRQ,
  CheckoutSessionRS,
  CheckoutSessionStatusRS,
  refundPayload,
  RefundPaymentRS,
  RetrievePaymentIntentRS,
  reverseTransferPayload,
  ReverseTransferRS,
  SessionMetadata,
  SessionUpdatePayload,
} from "@/api/stripe/stripe.models";

const xsrfCookieName = "XSRF-TOKEN";

export class StripeClient {
  private httpClient: AxiosInstance;

  constructor() {
    this.httpClient = axios.create({
      baseURL: config.StripeUrl,
      timeout: parseInt(config.ApiTimeout),
      withCredentials: true,
      xsrfCookieName,
      xsrfHeaderName: "X-CSRF-Token",
      headers: {
        "Content-Type": "application/json",
      },
    });

    this.httpClient.interceptors.request.use(intercept.Request);
    this.httpClient.interceptors.response.use(intercept.Response, intercept.Error(StripeClient.ErrorResponse));
  }

  private static ErrorResponse(err: AxiosErrorWithResponse) {
    if (err.response?.status === 401) {
      return StripeClient.on401(err);
    }

    if (err.response.status >= 400 && err.response.status < 500) {
      const wpsErr = err.response.data;
      return Promise.reject({
        status: err.response.status,
        wpsErr,
        ...err,
      });
    }

    // not 4xx
    return Promise.reject({
      status: err.response.status,
      ...err,
    });
  }

  private static on401(err: any): Promise<any> {
    return Promise.reject(err);
  }

  public createCheckoutSession(request: CheckoutSessionRQ, errs?: MeErrHandlers): Observable<CheckoutSessionRS> {
    return from(this.httpClient.post<any>(`/checkout-create-session/`, JSON.stringify(request))).pipe(
      map((rs) => rs.data),
      catchError(handleErrs(() => {}, errs)),
    );
  }

  public updateCheckoutSession(payload: SessionUpdatePayload, errs?: MeErrHandlers): Observable<any> {
    return from(this.httpClient.post<any>(`/checkout-update-session/${payload.sessionID}`, JSON.stringify(payload.metadata))).pipe(
      map((rs) => rs.data),
      catchError(handleErrs(() => {}, errs)),
    );
  }

  public retrieveCheckoutSession(sessionID: string, errs?: MeErrHandlers): Observable<CheckoutSessionStatusRS> {
    return from(this.httpClient.get<any>(`/checkout-retrieve-session/${sessionID}`)).pipe(
      map((rs) => rs.data),
      catchError(handleErrs(() => {}, errs)),
    );
  }
  public retrieveBalance(errs?: MeErrHandlers): Observable<any> {
    return from(this.httpClient.get<any>(`/retrieve-balance/`)).pipe(
      map((rs) => rs.data),
      catchError(handleErrs(() => {}, errs)),
    );
  }
  /**
   * finalizes the payment, this is called on booking success
   */
  public capturePayment(paymentIntentID: string, amountToCapture?: number, errs?: MeErrHandlers): Observable<any> {
    const payload = {
      ...(amountToCapture !== undefined && { amount_to_capture: amountToCapture }),
    };
    return from(this.httpClient.post<any>(`/capture-payment/${paymentIntentID}`, payload)).pipe(
      map((rs) => rs.data),
      catchError(handleErrs(() => {}, errs)),
    );
  }

  /**
   * cancels the payment, this is called on booking failure
   */
  public cancelPayment(paymentIntentID: string, errs?: MeErrHandlers): Observable<any> {
    return from(this.httpClient.post<any>(`/cancel-payment/${paymentIntentID}`)).pipe(
      map((rs) => rs.data),
      catchError(handleErrs(() => {}, errs)),
    );
  }

  /**
   * refunds the payment, this is called on booking cancellation
   */
  public refundPayment(payload: refundPayload, errs?: MeErrHandlers): Observable<RefundPaymentRS> {
    return from(this.httpClient.post<any>(`/refund-payment/${payload.paymentIntent}/${payload.amount}`)).pipe(
      map((rs) => rs.data),
      catchError(handleErrs(() => {}, errs)),
    );
  }

  /**
   * attempts to retrieve a payment intent
   */
  public retrievePaymentIntent(paymentIntentID: string, errs?: MeErrHandlers): Observable<RetrievePaymentIntentRS> {
    return from(this.httpClient.get<any>(`/payment-intent/${paymentIntentID}`)).pipe(
      map((rs) => rs.data),
      catchError(handleErrs(() => {}, errs)),
    );
  }

  /**
   * attempts to update a payment intent
   */
  public updatePaymentIntent(paymentIntentID: string, payload: SessionMetadata, errs?: MeErrHandlers): Observable<any> {
    return from(this.httpClient.post<any>(`/update-payment/${paymentIntentID}`, JSON.stringify(payload))).pipe(
      map((rs) => rs.data),
      catchError(handleErrs(() => {}, errs)),
    );
  }

  /**
   * attempts to refund by reversing the transfer based on the given charge id
   */
  public reverseTransfer(payload: reverseTransferPayload, errs?: MeErrHandlers): Observable<ReverseTransferRS> {
    return from(this.httpClient.post<any>(`/reverse-payment/${payload.chargeId}/${payload.amount}`)).pipe(
      map((rs) => rs.data),
      catchError(handleErrs(() => {}, errs)),
    );
  }
}

const stripeClient = new StripeClient();

export default stripeClient;
